import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Mobile drill-down menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element wrapping the drawer.
 * @property {HTMLDivElement} menuDrawer - The fixed clipping drawer region.
 * @property {HTMLDivElement} surface - The sliding cream surface.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'surface'];

  /** @type {number | null} Pending requestAnimationFrame id from open(). */
  #openRaf = null;

  /** @type {(() => void) | null} Tears down a pending close (listener + timer). */
  #closeTeardown = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('click', this.#onDocumentClick);
    this.#populateFeatures();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * Close the drawer when the cart control is clicked (e.g. opening the cart
   * drawer over the open menu).
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.isOpen) return;
    const target = event.target;
    if (target instanceof Element && target.closest('cart-icon')) {
      this.close();
    }
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Open or close the drawer. Cancels the native <details> toggle so the `open`
   * attribute is driven only by open()/close(), keeping it in sync with the
   * `menu-open` class (which the X icon + header colour key off).
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    const { details } = this.refs;
    // Cancel a pending close so it can't strip `open`/state after we reopen.
    this.#closeTeardown?.();

    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    // Populate here too: at connect time the JSON payloads (rendered by the
    // desktop menu instance) may not be parsed yet. Idempotent — fills only
    // empty slots.
    this.#populateFeatures();

    // Content is now present; add the class on the next frame so the entry
    // transition runs from its hidden starting state.
    this.#openRaf = requestAnimationFrame(() => {
      this.#openRaf = null;
      details.classList.add('menu-open');
      trapFocus(details);
    });
  }

  close() {
    const { details, surface } = this.refs;
    // Tear down any in-flight close so we don't stack listeners/timers.
    this.#closeTeardown?.();
    // Cancel a pending open frame so it can't re-add `menu-open` after we close.
    if (this.#openRaf !== null) {
      cancelAnimationFrame(this.#openRaf);
      this.#openRaf = null;
    }

    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    removeTrapFocus();

    const finalise = () => {
      this.#closeTeardown?.();
      details.removeAttribute('open');
      this.#resetPanels();
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target === surface && event.propertyName === 'opacity') finalise();
    };

    surface.addEventListener('transitionend', onEnd);
    // Fallback in case the transition doesn't fire (reduced motion, etc.)
    const timer = setTimeout(finalise, 600);

    this.#closeTeardown = () => {
      surface.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      this.#closeTeardown = null;
    };
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key === 'Escape') this.close();
  };

  /**
   * Drill into a sub-panel.
   * @param {Event} event - Click on a [data-panel-target] button.
   */
  drill(event) {
    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-panel-target]') : null
    );
    if (!trigger) return;

    const id = trigger.dataset.panelTarget;
    if (!id) return;

    const target = this.querySelector(`.menu-drawer__panel[data-panel="${CSS.escape(id)}"]`);
    const root = this.#rootPanel;
    if (!(target instanceof HTMLElement) || !root) return;

    root.removeAttribute('data-active');
    root.setAttribute('data-behind', '');
    target.setAttribute('data-active', '');

    target.scrollTop = 0;
    target.querySelector('a, button')?.focus({ preventScroll: true });
  }

  /** Return to the root panel. */
  panelBack() {
    const root = this.#rootPanel;
    this.querySelectorAll('.menu-drawer__panel:not([data-panel="root"])').forEach((panel) =>
      panel.removeAttribute('data-active')
    );
    if (root) {
      root.removeAttribute('data-behind');
      root.setAttribute('data-active', '');
    }
  }

  get #rootPanel() {
    return this.querySelector('.menu-drawer__panel[data-panel="root"]');
  }

  #resetPanels() {
    this.querySelectorAll('.menu-drawer__panel').forEach((panel) => {
      panel.removeAttribute('data-active');
      panel.removeAttribute('data-behind');
    });
    this.#rootPanel?.setAttribute('data-active', '');
  }

  /**
   * Fills each section feature slot with the matching submenu image. Reuses the
   * JSON payloads emitted by the _submenu-image blocks (matched by title), so
   * the desktop and drawer share the same image configuration.
   */
  #populateFeatures() {
    const slots = this.querySelectorAll('.menu-drawer__feature--section[data-submenu-title]');
    if (!slots.length) return;

    /** @type {Record<string, any>} */
    const byTitle = {};
    document.querySelectorAll('script.submenu-image-data').forEach((node) => {
      try {
        const data = JSON.parse(node.textContent ?? '');
        if (data && data.title) byTitle[data.title] = data;
      } catch {
        /* ignore */
      }
    });

    for (const slot of slots) {
      if (slot.querySelector('img')) continue;
      const data = byTitle[slot.dataset.submenuTitle ?? ''];
      if (!data || !data.src) continue;

      const img = document.createElement('img');
      img.className = 'menu-drawer__feature-image';
      img.loading = 'lazy';
      img.src = data.src;
      img.alt = data.alt ?? '';
      if (data.width) img.width = data.width;
      if (data.height) img.height = data.height;

      const media = document.createElement('span');
      media.className = 'menu-drawer__feature-media';
      media.appendChild(img);

      const wrapper = data.link ? document.createElement('a') : document.createElement('span');
      if (data.link) /** @type {HTMLAnchorElement} */ (wrapper).href = data.link;
      wrapper.appendChild(media);

      const visibility = data.textVisibility || 'mobile';
      if (data.text && (visibility === 'mobile' || visibility === 'both')) {
        const text = document.createElement('span');
        text.className = 'menu-drawer__feature-text';
        text.textContent = data.text;
        wrapper.appendChild(text);
      }

      slot.appendChild(wrapper);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
